import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCpu, FiClock, FiToggleLeft, FiToggleRight, FiX, FiSave } from 'react-icons/fi';
import {
  AIAssistant,
  getAssistantsBySector,
  createAssistant,
  updateAssistant,
  deleteAssistant,
} from '../services/AIAssistantService';
import SessionService from '../services/SessionService';
import { getSectors, Sector } from '../services/SectorService';
import './AIAssistantPage.css';

const WEEKDAYS = [
  { key: '0', label: 'Dom' },
  { key: '1', label: 'Seg' },
  { key: '2', label: 'Ter' },
  { key: '3', label: 'Qua' },
  { key: '4', label: 'Qui' },
  { key: '5', label: 'Sex' },
  { key: '6', label: 'Sáb' },
];

const AIAssistantPage: React.FC = () => {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [assistants, setAssistants] = useState<AIAssistant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState<AIAssistant | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formPrompt, setFormPrompt] = useState('');
  const [formKnowledgeBase, setFormKnowledgeBase] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formStartTime, setFormStartTime] = useState('');
  const [formEndTime, setFormEndTime] = useState('');
  const [formDays, setFormDays] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Obter setor do sidemenu (SessionService)
  useEffect(() => {
    const sectorId = SessionService.getSectorId();
    if (sectorId) {
      setSelectedSectorId(Number(sectorId));
    }

    // Escutar mudança de setor no sidemenu
    const handleSectorChange = () => {
      const newSectorId = SessionService.getSectorId();
      setSelectedSectorId(newSectorId ? Number(newSectorId) : null);
    };

    window.addEventListener('sectorChanged', handleSectorChange);
    window.addEventListener('storage', handleSectorChange);
    return () => {
      window.removeEventListener('sectorChanged', handleSectorChange);
      window.removeEventListener('storage', handleSectorChange);
    };
  }, []);

  // Carregar setores para obter roleNoSetor
  useEffect(() => {
    const token = SessionService.getToken();
    if (token) {
      getSectors(token).then(data => setSectors(data)).catch(() => setSectors([]));
    }
  }, []);

  // Determinar se pode gerenciar o setor atualmente selecionado
  const canManageCurrentSector = selectedSectorId
    ? (sectors.find(s => s.id === selectedSectorId)?.roleNoSetor ?? 1) >= 2
    : false;

  // Load assistants when sector changes
  useEffect(() => {
    if (selectedSectorId) {
      loadAssistants(selectedSectorId);
    } else {
      setAssistants([]);
    }
  }, [selectedSectorId]);

  const loadAssistants = async (sectorId: number) => {
    setIsLoading(true);
    try {
      const data = await getAssistantsBySector(sectorId);
      setAssistants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('[AIAssistant] Erro ao carregar assistentes:', error);
      setAssistants([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormTitle('');
    setFormPrompt('');
    setFormKnowledgeBase('');
    setFormIsActive(true);
    setFormStartTime('');
    setFormEndTime('');
    setFormDays([]);
    setFormErrors({});
    setEditingAssistant(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleOpenEdit = (assistant: AIAssistant) => {
    setEditingAssistant(assistant);
    setFormTitle(assistant.title);
    setFormPrompt(assistant.prompt || '');
    setFormKnowledgeBase(assistant.knowledgeBase || '');
    setFormIsActive(assistant.isActive);
    setFormStartTime(assistant.businessHoursStart || '');
    setFormEndTime(assistant.businessHoursEnd || '');
    setFormDays(assistant.businessDays ? assistant.businessDays.split(',').filter(Boolean) : []);
    setFormErrors({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const toggleDay = (day: string) => {
    setFormDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!formTitle.trim()) errors.title = 'Título é obrigatório';
    if (!formPrompt.trim()) errors.prompt = 'Prompt é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !selectedSectorId) return;

    setIsSaving(true);
    try {
      const payload = {
        title: formTitle.trim(),
        prompt: formPrompt.trim() || null,
        knowledgeBase: formKnowledgeBase.trim() || null,
        isActive: formIsActive,
        businessHoursStart: formStartTime || null,
        businessHoursEnd: formEndTime || null,
        businessDays: formDays.length > 0 ? formDays.sort().join(',') : null,
      };

      if (editingAssistant) {
        await updateAssistant(editingAssistant.id, payload);
      } else {
        await createAssistant({ ...payload, sectorId: selectedSectorId });
      }

      await loadAssistants(selectedSectorId);
      handleCloseForm();
    } catch (error: any) {
      console.error('[AIAssistant] Erro ao salvar:', error);
      const msg = error?.response?.data?.message || 'Erro ao salvar assistente';
      setFormErrors({ general: msg });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId || !selectedSectorId) return;
    try {
      await deleteAssistant(deletingId);
      await loadAssistants(selectedSectorId);
    } catch (error) {
      console.error('[AIAssistant] Erro ao excluir:', error);
    } finally {
      setShowDeleteModal(false);
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (assistant: AIAssistant) => {
    if (!selectedSectorId) return;
    try {
      await updateAssistant(assistant.id, {
        title: assistant.title,
        prompt: assistant.prompt,
        knowledgeBase: assistant.knowledgeBase,
        isActive: !assistant.isActive,
        businessHoursStart: assistant.businessHoursStart,
        businessHoursEnd: assistant.businessHoursEnd,
        businessDays: assistant.businessDays,
      });
      await loadAssistants(selectedSectorId);
    } catch (error) {
      console.error('[AIAssistant] Erro ao alternar status:', error);
    }
  };

  const formatDays = (days: string | null): string => {
    if (!days) return 'Todos os dias';
    const dayList = days.split(',');
    return dayList.map(d => WEEKDAYS.find(w => w.key === d)?.label || d).join(', ');
  };

  const formatTime = (time: string | null): string => {
    if (!time) return '--:--';
    return time.substring(0, 5);
  };

  return (
    <div className="ai-page">
      <div className="ai-page-header">
        <div className="ai-header-content">
          <h1><FiCpu /> Assistente IA</h1>
          <p className="ai-header-description">Configure os assistentes de inteligência artificial para cada setor</p>
        </div>
      </div>

      {!selectedSectorId ? (
        <div className="ai-empty-state">
          <FiCpu size={48} />
          <h3>Nenhum setor selecionado</h3>
          <p>Selecione um setor no menu lateral para configurar o assistente de IA</p>
        </div>
      ) : isLoading ? (
        <div className="ai-loading">
          <div className="ai-spinner"></div>
          <span>Carregando assistentes...</span>
        </div>
      ) : (
        <div className="ai-content">
          <div className="ai-content-header">
            <span className="ai-count">{assistants.length} assistente{assistants.length !== 1 ? 's' : ''}</span>
            {canManageCurrentSector && (
              <button className="ai-add-btn" onClick={handleOpenCreate}>
                <FiPlus /> Novo Assistente
              </button>
            )}
          </div>

          {assistants.length === 0 ? (
            <div className="ai-empty-state">
              <FiCpu size={40} />
              <h3>Nenhum assistente configurado</h3>
              <p>Crie um assistente de IA para este setor</p>
            </div>
          ) : (
            <div className="ai-cards">
              {assistants.map(a => (
                <div key={a.id} className={`ai-card ${a.isActive ? 'active' : 'inactive'}`}>
                  <div className="ai-card-header">
                    <div className="ai-card-title-row">
                      <h3>{a.title}</h3>
                      <div className={`ai-card-status ${a.isActive ? 'active' : 'inactive'}`}>
                        {a.isActive ? 'Ativo' : 'Inativo'}
                      </div>
                    </div>
                    {canManageCurrentSector && (
                      <div className="ai-card-actions">
                        <button
                          className="ai-card-btn ai-toggle-btn"
                          onClick={() => handleToggleActive(a)}
                          title={a.isActive ? 'Desativar' : 'Ativar'}
                        >
                          {a.isActive ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                        </button>
                        <button className="ai-card-btn" onClick={() => handleOpenEdit(a)} title="Editar">
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="ai-card-btn ai-delete-btn"
                          onClick={() => { setDeletingId(a.id); setShowDeleteModal(true); }}
                          title="Excluir"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="ai-card-body">
                    <div className="ai-card-field">
                      <label>Prompt:</label>
                      <p className="ai-card-prompt">{a.prompt ? (a.prompt.length > 200 ? a.prompt.substring(0, 200) + '...' : a.prompt) : 'Não definido'}</p>
                    </div>

                    {a.knowledgeBase && (
                      <div className="ai-card-field">
                        <label>Base de conhecimento:</label>
                        <p className="ai-card-kb">{a.knowledgeBase.length > 100 ? a.knowledgeBase.substring(0, 100) + '...' : a.knowledgeBase}</p>
                      </div>
                    )}

                    <div className="ai-card-schedule">
                      <FiClock size={14} />
                      <span>
                        {a.businessHoursStart && a.businessHoursEnd
                          ? `${formatTime(a.businessHoursStart)} - ${formatTime(a.businessHoursEnd)}`
                          : '24 horas'}
                      </span>
                      <span className="ai-card-days">{formatDays(a.businessDays)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="ai-modal-overlay" onClick={handleCloseForm}>
          <div className="ai-modal" onClick={e => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h3>{editingAssistant ? 'Editar Assistente' : 'Novo Assistente'}</h3>
              <button className="ai-modal-close" onClick={handleCloseForm}><FiX /></button>
            </div>

            <div className="ai-modal-body">
              {formErrors.general && (
                <div className="ai-form-error-general">{formErrors.general}</div>
              )}

              <div className="ai-form-group">
                <label>Título *</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="Ex: Assistente de Vendas"
                  className={formErrors.title ? 'error' : ''}
                />
                {formErrors.title && <span className="ai-form-error">{formErrors.title}</span>}
              </div>

              <div className="ai-form-group">
                <label>Prompt (Instruções da IA) *</label>
                <textarea
                  value={formPrompt}
                  onChange={e => setFormPrompt(e.target.value)}
                  placeholder="Descreva como a IA deve se comportar, o tom de voz, regras, etc..."
                  rows={6}
                  className={formErrors.prompt ? 'error' : ''}
                />
                {formErrors.prompt && <span className="ai-form-error">{formErrors.prompt}</span>}
              </div>

              <div className="ai-form-group">
                <label>Base de Conhecimento</label>
                <textarea
                  value={formKnowledgeBase}
                  onChange={e => setFormKnowledgeBase(e.target.value)}
                  placeholder="Informações adicionais que a IA deve conhecer (produtos, preços, FAQ, etc.)"
                  rows={4}
                />
              </div>

              <div className="ai-form-row">
                <div className="ai-form-group ai-form-half">
                  <label>Horário de Início</label>
                  <input
                    type="time"
                    value={formStartTime}
                    onChange={e => setFormStartTime(e.target.value)}
                  />
                </div>
                <div className="ai-form-group ai-form-half">
                  <label>Horário de Fim</label>
                  <input
                    type="time"
                    value={formEndTime}
                    onChange={e => setFormEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="ai-form-group">
                <label>Dias da Semana</label>
                <div className="ai-days-selector">
                  {WEEKDAYS.map(day => (
                    <button
                      key={day.key}
                      type="button"
                      className={`ai-day-btn ${formDays.includes(day.key) ? 'selected' : ''}`}
                      onClick={() => toggleDay(day.key)}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <span className="ai-form-hint">Deixe vazio para funcionar todos os dias</span>
              </div>

              <div className="ai-form-group ai-form-toggle">
                <label>Status</label>
                <button
                  type="button"
                  className={`ai-status-toggle ${formIsActive ? 'active' : ''}`}
                  onClick={() => setFormIsActive(!formIsActive)}
                >
                  {formIsActive ? <FiToggleRight size={24} /> : <FiToggleLeft size={24} />}
                  <span>{formIsActive ? 'Ativo' : 'Inativo'}</span>
                </button>
              </div>
            </div>

            <div className="ai-modal-footer">
              <button className="ai-btn ai-btn-secondary" onClick={handleCloseForm}>Cancelar</button>
              <button className="ai-btn ai-btn-primary" onClick={handleSave} disabled={isSaving}>
                <FiSave />
                {isSaving ? 'Salvando...' : editingAssistant ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="ai-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="ai-modal ai-modal-small" onClick={e => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h3>Excluir Assistente</h3>
              <button className="ai-modal-close" onClick={() => setShowDeleteModal(false)}><FiX /></button>
            </div>
            <div className="ai-modal-body">
              <p>Tem certeza que deseja excluir este assistente? Esta ação não pode ser desfeita.</p>
            </div>
            <div className="ai-modal-footer">
              <button className="ai-btn ai-btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
              <button className="ai-btn ai-btn-danger" onClick={handleDelete}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistantPage;
